import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, GripVertical, X, Plus, ChevronDown } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { cn } from '@/lib/utils';

const clampCount = (n) => Math.min(6, Math.max(2, Number(n) || 3));

export default function PopularSongsCatalogueSettings({
  userId,
  profile,
  songs,
  refreshProfile,
  className = '',
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Expanded panel: off = “what is this?” copy; on = count + pins. Chevron toggles either way. */
  const [expanded, setExpanded] = useState(false);

  const show = profile?.show_popular_songs_public === true;
  const count = clampCount(profile?.popular_songs_display_count);
  const featuredIds = Array.isArray(profile?.popular_songs_featured_ids)
    ? profile.popular_songs_featured_ids
    : [];

  useEffect(() => {
    if (show) setExpanded(true);
  }, [show]);

  const songById = useMemo(() => {
    const m = new Map();
    (songs || []).forEach((s) => m.set(s.id, s));
    return m;
  }, [songs]);

  const trimmedInvalidRef = useRef(false);
  useEffect(() => {
    const valid = featuredIds.filter((id) => songById.has(id));
    if (valid.length === featuredIds.length || !userId) return;
    if (trimmedInvalidRef.current) return;
    trimmedInvalidRef.current = true;
    (async () => {
      try {
        await updateDoc(doc(db, 'profiles', userId), { popular_songs_featured_ids: valid });
        await refreshProfile?.();
      } catch (e) {
        console.warn('Could not trim invalid featured song ids', e);
      } finally {
        trimmedInvalidRef.current = false;
      }
    })();
  }, [featuredIds, songById, userId, refreshProfile]);

  const persist = useCallback(
    async (patch, { silent } = {}) => {
      if (!userId) return;
      setSaving(true);
      try {
        await updateDoc(doc(db, 'profiles', userId), patch);
        await refreshProfile?.();
        if (!silent) {
          toast({ title: 'Saved', description: 'Your spotlight settings are saved.' });
        }
      } catch (e) {
        console.error(e);
        toast({
          title: 'Could not save',
          description: 'Something went wrong — try again in a moment.',
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
    },
    [userId, refreshProfile, toast]
  );

  const onToggle = async (checked) => {
    await persist({ show_popular_songs_public: !!checked });
    if (!checked) setExpanded(false);
  };

  const onCountChange = async (value) => {
    const next = clampCount(parseInt(value, 10));
    const trimmedFeatured = featuredIds.slice(0, next);
    await persist({
      popular_songs_display_count: next,
      popular_songs_featured_ids: trimmedFeatured,
    });
  };

  const setFeaturedIds = async (nextIds) => {
    const capped = nextIds.filter((id) => songById.has(id)).slice(0, 6);
    await persist({ popular_songs_featured_ids: capped }, { silent: true });
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(featuredIds);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    await setFeaturedIds(items);
  };

  const removeFeatured = async (id) => {
    await setFeaturedIds(featuredIds.filter((x) => x !== id));
  };

  const addFeatured = async (id) => {
    if (!id || featuredIds.includes(id)) return;
    if (featuredIds.length >= 6) {
      toast({
        title: 'That’s enough for now',
        description: 'You can pin up to six songs. The number you chose above controls how many appear when you’re live.',
        variant: 'destructive',
      });
      return;
    }
    await setFeaturedIds([...featuredIds, id]);
    setPickerOpen(false);
  };

  const addableOptions = useMemo(() => {
    const active = (songs || []).filter((s) => !s.archived);
    return active
      .filter((s) => !featuredIds.includes(s.id))
      .sort((a, b) => {
        const diff = (b.lifetime_request_count || 0) - (a.lifetime_request_count || 0);
        if (diff !== 0) return diff;
        return (a.title || '').localeCompare(b.title || '');
      });
  }, [songs, featuredIds]);

  return (
    <Card className={cn('bg-gray-800/80 border-gray-700', className)}>
      <CardHeader className="py-3 px-4 pb-2 space-y-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-600/20 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg text-white leading-tight">
              Pin/Highlight Songs
            </CardTitle>
            {show && expanded && (
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                A few crowd-pleasers up top when you’re live — great for people who’ve just scanned your QR.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/80 transition-colors"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronDown
              className={cn('w-5 h-5 transition-transform duration-200', expanded && 'rotate-180')}
            />
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="popular-toggle" className="sr-only">
              Show pinned highlights when live
            </Label>
            <Switch
              id="popular-toggle"
              checked={show}
              disabled={saving || !userId}
              onCheckedChange={onToggle}
            />
          </div>
        </div>
      </CardHeader>

      {!show && expanded && (
        <CardContent className="pt-0 px-4 pb-4">
          <p className="text-sm text-gray-300 leading-relaxed">
            When you’re live, we can put a handful of your most-requested songs right up front so new fans
            spot a title they know straight away.
          </p>
        </CardContent>
      )}

      {show && expanded && (
        <CardContent className="space-y-4 pt-0 px-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-gray-300 text-sm">How many songs to spotlight</Label>
            <Select value={String(count)} onValueChange={onCountChange} disabled={saving}>
              <SelectTrigger className="w-[100px] bg-gray-900 border-gray-600 text-white">
                <SelectValue placeholder="3" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-600 text-white">
                {[2, 3, 4, 5, 6].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-sm text-gray-400 mb-2">
              Prefer your own order? Pin songs below — or leave this empty and we’ll use your request history.
            </p>
            {featuredIds.length === 0 ? (
              <p className="text-xs text-gray-500 mb-2">Nothing pinned — we’ll choose from your most-requested tracks.</p>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="popular-featured">
                  {(provided) => (
                    <ul
                      className="space-y-2 rounded-lg border border-gray-700 p-2 bg-gray-900/50"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {featuredIds.map((id, index) => {
                        const s = songById.get(id);
                        if (!s) return null;
                        const lc = s.lifetime_request_count || 0;
                        return (
                          <Draggable key={id} draggableId={id} index={index}>
                            {(dragProvided) => (
                              <li
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className="flex items-center gap-2 rounded-md bg-gray-800 px-2 py-2 text-sm text-white"
                              >
                                <span {...dragProvided.dragHandleProps} className="text-gray-500 cursor-grab">
                                  <GripVertical className="w-4 h-4" />
                                </span>
                                <span className="flex-1 truncate">
                                  {s.title}
                                  {s.original_artist ? (
                                    <span className="text-gray-400"> — {s.original_artist}</span>
                                  ) : null}
                                  {lc > 0 ? (
                                    <span className="text-gray-500 text-xs ml-2">({lc} requests)</span>
                                  ) : null}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-gray-400 hover:text-red-400"
                                  onClick={() => removeFeatured(id)}
                                  disabled={saving}
                                  aria-label={`Remove ${s.title}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </li>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-200"
                onClick={() => setPickerOpen(true)}
                disabled={saving}
              >
                <Plus className="w-4 h-4 mr-1" />
                Pin a song
              </Button>
              {featuredIds.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-gray-400"
                  onClick={() => persist({ popular_songs_featured_ids: [] })}
                  disabled={saving}
                >
                  Use automatic picks
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pin a song</DialogTitle>
            <DialogDescription className="text-gray-400">
              Pinned songs appear first. Any remaining slots use your most-requested songs.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
            {addableOptions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-gray-800 text-gray-200"
                  onClick={() => addFeatured(s.id)}
                >
                  <span className="font-medium">{s.title}</span>
                  {s.original_artist ? (
                    <span className="text-gray-400"> — {s.original_artist}</span>
                  ) : null}
                  {(s.lifetime_request_count || 0) > 0 ? (
                    <span className="text-gray-500 text-xs ml-2">({s.lifetime_request_count} requests)</span>
                  ) : null}
                </button>
              </li>
            ))}
            {addableOptions.length === 0 && (
              <p className="text-sm text-gray-500">
                Every song is already pinned, or your catalogue is empty.
              </p>
            )}
          </ul>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setPickerOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
